import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

const OPERATIONS_SKELETON_SURFACE =
  "border border-zinc-200 bg-white shadow-[var(--shadow-border)] dark:border-zinc-800 dark:bg-zinc-950";

const attributeColumns = [44, 112, 144, 144, 220, 280, 216];
const dailyStatsChartBars = [
  12, 20, 16, 22, 14, 10, 8, 5, 7, 12, 16, 10, 58, 42, 48, 60, 40, 7, 12, 82,
  70, 100, 24,
];

type OperationsLoadingSkeletonProps = {
  label: string;
  section?: string;
};

export function OperationsLoadingSkeleton({
  label,
  section,
}: OperationsLoadingSkeletonProps) {
  const isAttributeRecords = section === "attribute-records";
  const isSubmissionReview = section === "submission-review";
  const isDailyStats = section === "dashboard-daily-stats";

  return (
    <div
      data-testid="operations-loading-skeleton"
      className="min-h-full bg-zinc-50 text-foreground dark:bg-zinc-950"
      role="status"
      aria-busy="true"
      aria-label={label}
      aria-live="polite"
    >
      <div
        aria-hidden="true"
        className="[&_.animate-pulse]:bg-zinc-200/80 dark:[&_.animate-pulse]:bg-zinc-800"
      >
        <div className="sticky top-0 z-30 border-b border-zinc-200 bg-white px-4 py-2 shadow-[var(--shadow-border)] dark:border-zinc-800 dark:bg-zinc-950 sm:px-6 lg:px-8">
          <div className="flex h-9 items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <Skeleton className="h-8 w-40 shrink-0 rounded-md" />
              <Skeleton className="hidden h-4 w-32 rounded-sm sm:block" />
              <Skeleton className="hidden h-5 w-24 rounded-md lg:block" />
            </div>
            <Skeleton className="size-9 shrink-0 rounded-md" />
          </div>
        </div>

        <div className="min-h-[calc(100dvh-53px)]">
          <div className="min-w-0">
            {isAttributeRecords ? (
              <div className="px-0 py-0 md:px-6 md:py-3 lg:px-8">
                <AttributeRegisterLoadingSkeleton />
              </div>
            ) : isSubmissionReview ? (
              <div className="px-3 py-3 sm:px-4 lg:px-6">
                <SubmissionReviewLoadingSkeleton />
              </div>
            ) : isDailyStats ? (
              <div className="px-4 py-5 sm:px-6 lg:px-8">
                <DailyStatsLoadingSkeleton />
              </div>
            ) : (
              <StandardOperationLoadingSkeleton />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StandardOperationLoadingSkeleton() {
  return (
    <div className="px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1500px]">
        <div className="flex flex-col gap-4 border-b border-zinc-200 pb-4 dark:border-zinc-800 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <Skeleton className="h-3 w-24 rounded-sm" />
            <Skeleton className="h-7 w-64 max-w-full rounded-sm" />
            <Skeleton className="h-3 w-full max-w-[32rem] rounded-sm" />
          </div>
          <Skeleton className="h-10 w-28 rounded-md" />
        </div>
        <div className="flex gap-2 border-b border-zinc-200 py-3 dark:border-zinc-800">
          <Skeleton className="h-9 w-32 rounded-md" />
          <Skeleton className="h-9 w-24 rounded-md" />
          <Skeleton className="h-9 w-28 rounded-md" />
        </div>
        <div className="space-y-4 py-6">
          <div className="space-y-2">
            <Skeleton className="h-5 w-44 rounded-sm" />
            <Skeleton className="h-3 w-full max-w-[36rem] rounded-sm" />
          </div>
          <div className={cn(OPERATIONS_SKELETON_SURFACE, "p-4")}>
            <div className="space-y-3">
              <Skeleton className="h-4 w-36 rounded-sm" />
              <Skeleton className="h-10 w-full rounded-md" />
              <Skeleton className="h-10 w-full rounded-md" />
              <Skeleton className="h-10 w-2/3 rounded-md" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AttributeRegisterLoadingSkeleton() {
  return (
    <div
      data-testid="attribute-register-loading-skeleton"
      className="flex min-h-[32rem] flex-col lg:h-[calc(100dvh-8rem)]"
    >
      <div
        className={cn(
          OPERATIONS_SKELETON_SURFACE,
          "relative min-h-0 flex-1 overflow-hidden md:rounded-lg"
        )}
      >
        <div className="hidden h-full flex-col lg:flex">
          <div className="border-b border-zinc-200 px-3 py-3 dark:border-zinc-800">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-2">
                <Skeleton className="h-5 w-28 rounded-sm" />
                <Skeleton className="h-3 w-24 rounded-sm" />
              </div>
              <Skeleton className="h-8 w-20 rounded-md" />
            </div>
            <div className="mt-3 grid grid-cols-[minmax(14rem,20rem)_minmax(18rem,1fr)] gap-2">
              <div className="space-y-1.5">
                <Skeleton className="h-3 w-12 rounded-sm" />
                <div className="grid grid-cols-[minmax(0,1fr)_2.25rem] gap-2">
                  <Skeleton className="h-9 rounded-md" />
                  <Skeleton className="size-9 rounded-md" />
                </div>
              </div>
              <div className="self-end">
                <Skeleton className="h-9 w-full rounded-md" />
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2 border-t border-zinc-200 pt-2 dark:border-zinc-800">
              <Skeleton className="h-8 w-24 rounded-md" />
              <Skeleton className="h-8 w-20 rounded-md" />
              <Skeleton className="h-8 w-20 rounded-md" />
              <Skeleton className="h-8 w-24 rounded-md" />
              <Skeleton className="ml-auto h-8 w-20 rounded-md" />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
            <div className="flex h-12 border-b border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900">
              {attributeColumns.map((width, index) => (
                <div
                  key={width + index}
                  className="shrink-0 border-r border-zinc-200 px-2.5 py-2 dark:border-zinc-800"
                  style={{ width }}
                >
                  <Skeleton className="h-3 w-3/4 rounded-sm" />
                </div>
              ))}
            </div>
            {Array.from({ length: 7 }).map((_, rowIndex) => (
              <div
                key={rowIndex}
                className="flex h-10 border-b border-zinc-200 dark:border-zinc-800"
              >
                {attributeColumns.map((width, cellIndex) => (
                  <div
                    key={width + cellIndex}
                    className="shrink-0 border-r border-zinc-200 px-2.5 py-3 dark:border-zinc-800"
                    style={{ width }}
                  >
                    <Skeleton
                      className={cn(
                        "h-3 rounded-sm",
                        cellIndex === 0 ? "w-4" : "w-2/3"
                      )}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-800 dark:bg-zinc-900">
            <Skeleton className="h-8 w-28 rounded-md" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-3 w-16 rounded-sm" />
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="size-9 rounded-md" />
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-3 p-3 lg:hidden">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-2">
              <Skeleton className="h-5 w-28 rounded-sm" />
              <Skeleton className="h-3 w-20 rounded-sm" />
            </div>
            <Skeleton className="size-11 rounded-md" />
          </div>
          <Skeleton className="h-11 w-full rounded-md" />
          <Skeleton className="h-11 w-full rounded-md" />
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="space-y-3 border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-950"
            >
              <div className="flex items-center justify-between gap-3">
                <Skeleton className="h-4 w-32 rounded-sm" />
                <Skeleton className="h-5 w-14 rounded-md" />
              </div>
              <Skeleton className="h-3 w-full rounded-sm" />
              <Skeleton className="h-3 w-3/4 rounded-sm" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SubmissionReviewLoadingSkeleton() {
  return (
    <div data-testid="submission-review-loading-skeleton" className="space-y-3">
      <div className={cn(OPERATIONS_SKELETON_SURFACE, "rounded-lg p-3")}>
        <div className="grid grid-cols-2 gap-2 xl:grid-cols-[minmax(15rem,1.4fr)_repeat(3,minmax(8rem,1fr))_minmax(16rem,1.4fr)]">
          <Skeleton className="col-span-2 h-10 rounded-lg xl:col-span-1" />
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-10 rounded-lg" />
          ))}
          <div className="col-span-2 grid grid-cols-2 gap-2 xl:col-span-1">
            <Skeleton className="col-span-2 h-4 w-32 rounded-sm" />
            <Skeleton className="h-10 rounded-lg" />
            <Skeleton className="h-10 rounded-lg" />
          </div>
        </div>
      </div>

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_23rem]">
        <div
          className={cn(
            OPERATIONS_SKELETON_SURFACE,
            "overflow-hidden rounded-lg"
          )}
        >
          <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
            <div className="space-y-2">
              <Skeleton className="h-5 w-40 rounded-sm" />
              <Skeleton className="h-3 w-20 rounded-sm" />
            </div>
            <Skeleton className="size-10 rounded-lg" />
          </div>
          <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className="grid gap-3 px-4 py-3 lg:grid-cols-[minmax(0,1fr)_auto]"
              >
                <div className="min-w-0 space-y-2">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-36 rounded-sm" />
                    <Skeleton className="h-5 w-14 rounded-md" />
                    <Skeleton className="h-3 w-10 rounded-sm" />
                  </div>
                  <Skeleton className="h-3 w-3/4 rounded-sm" />
                  <div className="flex gap-2">
                    <Skeleton className="h-7 w-28 rounded-md" />
                    <Skeleton className="h-7 w-32 rounded-md" />
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {Array.from({ length: 4 }).map((__, actionIndex) => (
                    <Skeleton
                      key={actionIndex}
                      className="h-10 w-12 rounded-lg"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className={cn(
            OPERATIONS_SKELETON_SURFACE,
            "hidden min-h-[32rem] rounded-lg p-4 xl:block"
          )}
        >
          <div className="space-y-4">
            <div className="space-y-2 border-b border-zinc-200 pb-3 dark:border-zinc-800">
              <Skeleton className="h-5 w-44 rounded-sm" />
              <Skeleton className="h-3 w-3/4 rounded-sm" />
            </div>
            <Skeleton className="h-40 w-full rounded-lg" />
            <div className="grid grid-cols-2 gap-2">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="space-y-1.5 py-1">
                  <Skeleton className="h-3 w-16 rounded-sm" />
                  <Skeleton className="h-4 w-28 max-w-full rounded-sm" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        className={cn(
          OPERATIONS_SKELETON_SURFACE,
          "flex flex-wrap gap-2 rounded-lg p-3"
        )}
      >
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className="flex min-h-10 min-w-32 items-center gap-2 rounded-lg bg-zinc-50 px-2.5 py-1.5 dark:bg-zinc-900"
          >
            <Skeleton className="h-6 w-8 rounded-sm" />
            <div className="space-y-1.5">
              <Skeleton className="h-2.5 w-16 rounded-sm" />
              <Skeleton className="h-2.5 w-20 rounded-sm" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DailyStatsLoadingSkeleton() {
  return (
    <div data-testid="daily-stats-loading-skeleton" className="space-y-5">
      <div
        className={cn(
          OPERATIONS_SKELETON_SURFACE,
          "flex flex-col gap-3 rounded-lg px-4 py-3 lg:flex-row lg:items-center lg:justify-between"
        )}
      >
        <div className="min-w-0 space-y-2">
          <Skeleton className="h-3 w-32 rounded-sm" />
          <Skeleton className="h-5 w-56 max-w-full rounded-sm" />
          <Skeleton className="h-3 w-full max-w-[36rem] rounded-sm" />
        </div>
        <Skeleton className="h-10 w-24 rounded-md" />
      </div>

      <div className={cn(OPERATIONS_SKELETON_SURFACE, "rounded-md")}>
        <div className="border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
          <Skeleton className="h-4 w-40 rounded-sm" />
        </div>
        <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="flex min-h-11 items-center justify-between gap-4 px-4 py-2"
            >
              <Skeleton className="h-3 w-40 rounded-sm" />
              <Skeleton className="h-4 w-8 rounded-sm" />
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className={cn(OPERATIONS_SKELETON_SURFACE, "rounded-lg p-3")}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-2">
                <Skeleton className="h-3 w-28 rounded-sm" />
                <Skeleton className="h-7 w-14 rounded-sm" />
              </div>
              <Skeleton className="h-6 w-14 rounded-md" />
            </div>
          </div>
        ))}
      </div>

      <div className={cn(OPERATIONS_SKELETON_SURFACE, "rounded-md p-4")}>
        <Skeleton className="h-4 w-40 rounded-sm" />
        <div className="mt-5 h-48 border-b border-l border-zinc-200 px-3 dark:border-zinc-800 sm:h-60">
          <div className="flex h-full items-end gap-2">
            {dailyStatsChartBars.map((height, index) => (
              <div
                key={index}
                className="min-w-0 flex-1 rounded-t bg-zinc-300 dark:bg-zinc-700"
                style={{ height: `${height}%` }}
              />
            ))}
          </div>
        </div>
        <div className="mt-2 flex justify-between gap-3">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              key={index}
              className="h-2.5 w-16 max-w-[18%] rounded-sm"
            />
          ))}
        </div>
      </div>

      <div
        className={cn(
          OPERATIONS_SKELETON_SURFACE,
          "overflow-hidden rounded-md"
        )}
      >
        <div className="px-4 py-3">
          <Skeleton className="h-4 w-44 rounded-sm" />
        </div>
        <div className="border-t border-zinc-200 dark:border-zinc-800">
          {Array.from({ length: 5 }).map((_, rowIndex) => (
            <div
              key={rowIndex}
              className="grid grid-cols-[1.2fr_repeat(3,1fr)_0.5fr] gap-3 border-b border-zinc-200 px-4 py-3 last:border-b-0 dark:border-zinc-800"
            >
              {Array.from({ length: 5 }).map((__, cellIndex) => (
                <Skeleton
                  key={cellIndex}
                  className={cn(
                    "h-3 rounded-sm",
                    cellIndex === 0 ? "w-24" : "w-10 justify-self-end"
                  )}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
